import { useId } from 'react';

import { Collapse, Group, GroupProps, Stack } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { UnstyledButton } from '../../../../Buttons';
import { Switch } from '../../../../Inputs/Switch/Switch';
import { cx } from '../../../../Provider/Provider';
import { Text } from '../../../../Typography';
import classes from './SwitchCard.module.css';

type SwitchCardProps = {
  className?: string;
  title?: string;
  titleAdornment?: React.ReactNode;
  subtitle?: string | React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  children?: React.ReactNode;
  containerProps?: GroupProps & { mod?: GroupProps['mod'] };
};

const SwitchCard = ({
  className,
  title,
  titleAdornment,
  subtitle,
  disabled,
  error,
  checked,
  defaultChecked,
  onChange,
  children,
  containerProps: { mod: containerMod, ...containerProps } = {},
}: SwitchCardProps) => {
  /**
   * These IDs are used to link the title and subtitle to the switch
   * This is required for accessibility
   */
  const titleId = useId();
  const subtitleId = useId();

  const [_checked, handleChange] = useUncontrolled({
    value: checked,
    defaultValue: defaultChecked,
    finalValue: false,
    onChange,
  });

  return (
    <Group
      component={UnstyledButton}
      justify="space-between"
      className={cx(classes.SwitchCard, className)}
      mod={[{ disabled, error, 'has-children': !!children }, containerMod]}
      onClick={() => !disabled && handleChange(!_checked)}
      tabIndex={disabled ? -1 : 0}
      {...containerProps}
    >
      <Stack className={classes.SwitchCard__content}>
        {title && (
          <Group gap="xs" align="center">
            <Text className={classes.SwitchCard__title} id={titleId}>
              {title}
            </Text>
            {titleAdornment}
          </Group>
        )}
        {subtitle &&
          (typeof subtitle === 'string' ? (
            <Text
              className={classes.SwitchCard__subtitle}
              id={subtitleId}
              mod={{ 'has-title': !!title }}
            >
              {subtitle}
            </Text>
          ) : (
            subtitle
          ))}
      </Stack>
      <Switch
        classNames={{
          body: classes.SwitchCard__switchBody,
        }}
        size="lg"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        checked={_checked}
        onClick={(event) => event.stopPropagation()}
        tabIndex={-1}
        disabled={disabled}
      />
    </Group>
  );
};

type CollapsibleSwitchCardProps = React.ComponentProps<typeof SwitchCard> & {
  children: React.ReactNode;
};

const CollapsibleSwitchCard = ({
  children,
  checked,
  defaultChecked,
  onChange,
  className,
  containerProps,
  ...switchCardProps
}: CollapsibleSwitchCardProps) => {
  const [_checked, handleChange] = useUncontrolled({
    value: checked,
    defaultValue: defaultChecked,
    finalValue: false,
    onChange,
  });

  return (
    <Stack gap={0} className={cx(classes.SwitchCardCollapsible, className)}>
      <SwitchCard
        className={classes.SwitchCardCollapsible__switchCard}
        containerProps={{
          ...containerProps,
          mod: [{ 'collapsible-opened': _checked }, containerProps?.mod],
        }}
        checked={_checked}
        onChange={(checked) => {
          handleChange(checked);
        }}
        {...switchCardProps}
      />
      <Collapse in={_checked} className={classes.SwitchCardCollapsible__content}>
        {children}
      </Collapse>
    </Stack>
  );
};

SwitchCard.Collapsible = CollapsibleSwitchCard;

export { SwitchCard, type CollapsibleSwitchCardProps, type SwitchCardProps };
